import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { useToast } from '../../../../components/toastContext';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { surveyRespondentsApi } from '../surveyApi';
import { answerOf, isMachineValue } from '../utils/responseAnswer';
import { sampleAssessmentRemarks } from '../utils/sampleDevelopmentPlan';
import type { SurveyResponseView } from '../surveyTypes';


/**
 * One employee's answers to a questionnaire — what clicking a name on the Responses › Assessment
 * opens. The answers are read only: a response is the employee's own words.
 *
 * **ASSESS** is the one thing done here, behind `surveys.assess`: it reveals a Remarks field, and
 * Submit — with the confirm tick, as every write — marks the response assessed, which puts the
 * employee on Survey › Development Plan. Once, and never on a deleted response; the button says
 * nothing it cannot do, and an assessed response shows who assessed it, when, and why.
 *
 * The name is the one the list row already resolved and is passed in; nothing here resolves or
 * stores a name. An employee above the reader's access level arrives as a 403 from the server,
 * which is shown, never worked around.
 */
export function SurveyResponseViewModal({
  open,
  surveyId,
  respondentId,
  respondentName,
  onClose,
  onAssessed,
  assessOnOpen = false,
}: {
  open: boolean;
  surveyId: string;
  respondentId: string;
  respondentName: string;
  onClose: () => void;
  /** The response was assessed — the lists showing it refresh. */
  onAssessed?: () => void;
  /** Opened from a row's Assess: the Assessment section is open from the start, beneath the answers. */
  assessOnOpen?: boolean;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [view, setView] = useState<SurveyResponseView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  // Assessing: the remarks field shows once Assess is clicked.
  const [assessing, setAssessing] = useState(assessOnOpen);
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assessError, setAssessError] = useState<ApiError | null>(null);

  // Hide, don't disable: without the key there is no Assess at all. A deleted or already-assessed
  // response is STATE, so those never offer it either — the window says which instead.
  const canAssess = hasPermission('surveys.assess') && view !== null && view.isActive && view.assessedAt == null;

  // Assessing only where it can be done — opened with `assessOnOpen` onto a response that turns out
  // to be assessed already, the window simply shows the assessment instead.
  const inAssess = assessing && canAssess;

  async function assess() {
    if (!confirmed || saving) return;

    setSaving(true);
    setAssessError(null);

    try {
      const next = await surveyRespondentsApi.assess(surveyId, respondentId, remarks.trim() || null);

      setView(next);
      setAssessing(false);
      toast.success(`Assessed ${respondentName}'s response.`);
      onAssessed?.();
    } catch (caught) {
      setAssessError(caught instanceof ApiError ? caught : new ApiError('The response could not be assessed.', 0));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();

    surveyRespondentsApi
      .get(surveyId, respondentId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setView(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError('Could not load this response.', 0));
      });

    return () => controller.abort();
  }, [surveyId, respondentId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={respondentName}
      size="2xl"
      id="survey-response-view"
      // The tick appears only while assessing — the one write here — and so does Autogenerate,
      // which fills the remarks (product call, 09 Oct 2026).
      autogenerate={inAssess ? { onGenerate: () => setRemarks(sampleAssessmentRemarks()), disabled: saving } : undefined}
      confirm={
        inAssess
          ? { id: 'survey-response-assess-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }
          : undefined
      }
      footer={
        inAssess ? (
          <>
            <Button id="survey-response-assess-cancel" variant="secondary" onClick={() => setAssessing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              id="survey-response-assess-submit"
              variant="primary"
              onClick={() => void assess()}
              disabled={!confirmed || saving}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        ) : (
          <>
            <Button id="survey-response-view-close" variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canAssess ? (
              <Button
                id="survey-response-assess"
                variant="primary"
                onClick={() => {
                  setRemarks('');
                  setConfirmed(false);
                  setAssessError(null);
                  setAssessing(true);
                }}
              >
                Assess
              </Button>
            ) : null}
          </>
        )
      }
    >
      {error ? (
        <ErrorState message={error.message} correlationId={error.correlationId} />
      ) : !view ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ResponseAnswers view={view} respondentName={respondentName} />

          {inAssess ? (
            <div className="border-t border-slate-200 pt-1">
              {/* A real fieldset: the heading names this section, and is announced with its field. */}
              <FormFieldset legend="Assessment">
                {assessError ? (
                  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs text-red-700">{assessError.message}</p>
                  </div>
                ) : null}

                <p className="text-xs text-slate-500">
                  Assessing marks this response as reviewed, and puts {respondentName} on the survey&apos;s
                  Development Plan. It is done once.
                </p>

                <Textarea
                  id="survey-response-assess-remarks"
                  label="Remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  error={assessError?.fieldError('remarks')}
                  disabled={saving}
                  autoFocus
                />
              </FormFieldset>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/**
 * A response as it reads — the questionnaire, when it was answered, every question with its
 * answer, the respondent's remarks, and the assessment once made. Shared by the answers window and
 * the Development Plan's Response Preview step, so the two cannot read the same response two ways.
 */
/**
 * The answers in words. Takes only what it reads, so the employee's own preview — which never
 * carries an assessment — renders through the same component.
 */
export function ResponseAnswers({
  view,
  respondentName,
}: {
  view: Pick<
    SurveyResponseView,
    'questionnaireTitle' | 'respondedAt' | 'isActive' | 'questions' | 'remarks' | 'assessedAt' | 'assessedByName' | 'assessmentRemarks'
  >;
  respondentName: string;
}) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-slate-800">{view.questionnaireTitle}</p>
        <p className="text-xs text-slate-500">
          Responded{' '}
          <time dateTime={toIso(view.respondedAt)} className="font-mono">
            {formatDateTime(view.respondedAt)}
          </time>
          {view.isActive ? null : ' · this response is deleted'}
        </p>
      </div>

      {view.questions.length === 0 ? (
        <p className="text-sm text-slate-500">This questionnaire has no questions.</p>
      ) : null}

      {view.questions.map((question, index) => {
        const answer = answerOf(question, respondentName);

        return (
          <div key={question.questionId} className="flex gap-2 rounded-xl border border-slate-200 p-4">
            <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{index + 1}.</span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <p className="text-sm text-slate-800">
                {question.text}
                {question.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
              </p>
              {question.description ? (
                <p className="whitespace-pre-line text-xs text-slate-500">{question.description}</p>
              ) : null}
              {/* An unanswered question says so — a blank would read as a rendering fault. */}
              {answer ? (
                <p
                  className={`whitespace-pre-line text-sm text-slate-600 ${isMachineValue(question.type) ? 'font-mono' : ''}`}
                >
                  {answer}
                </p>
              ) : (
                <p className="text-sm text-slate-400">Not answered</p>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-col gap-1">
        <p className="text-sm text-slate-700">Remarks</p>
        {view.remarks ? (
          <p className="whitespace-pre-line text-sm text-slate-600">{view.remarks}</p>
        ) : (
          <p className="text-sm text-slate-400">—</p>
        )}
      </div>

      {/* The assessment, once made: who, when, and what they wrote. */}
      {view.assessedAt ? (
        <div role="status" className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs text-slate-600">
            Assessed by {view.assessedByName ?? 'an unknown account'} on{' '}
            <time dateTime={toIso(view.assessedAt)} className="font-mono">
              {formatDateTime(view.assessedAt)}
            </time>
            . This employee is on the Development Plan.
          </p>
          {view.assessmentRemarks ? (
            <p className="whitespace-pre-line text-sm text-slate-600">{view.assessmentRemarks}</p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
